import { casePath } from '@/api/runPath';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Drawer, Input, Segmented, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ThunderboltOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { listRuns, getRunState } from '@/api/runs';
import type { RunIndexItem, RunStateView } from '@/api/types';
import { normalizeApiError, isNotAvailable } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';
import { RunsEmptyState } from './RunsEmptyState';

const { Paragraph, Text } = Typography;

type StatusFilter = 'ALL' | 'ACTIVE' | 'COMPLETED';

function OutcomeTag({ r }: { r: RunIndexItem }) {
  if (!r.completedAt) {
    return (
      <Tag bordered={false} color="processing">
        ACTIVE
      </Tag>
    );
  }
  if (r.failure) {
    return (
      <Tag bordered={false} color="error" title={r.failure}>
        FAILED
      </Tag>
    );
  }
  return (
    <Tag bordered={false} color={r.outcome === 'BLOCKED' ? 'volcano' : 'success'}>
      {r.outcome ?? 'COMPLETED'}
    </Tag>
  );
}

function ts(v?: string | null): string {
  return v ? new Date(v).toLocaleString() : '—';
}

/** Node-status map rendered as compact rows (examiner-grade: what decided, what's open). */
function StateMap({ label, map }: { label: string; map: Record<string, string> }) {
  const entries = Object.entries(map);
  if (entries.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <Text strong style={{ fontSize: 12 }}>
        {label}
      </Text>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <Text className="ol-mono" style={{ fontSize: 11.5 }}>
            {k}
          </Text>
          <Text className="ol-mono" style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>
            {v}
          </Text>
        </div>
      ))}
    </div>
  );
}

export function RunsPage() {
  usePageTitle('Runs');
  const [searchParams, setSearchParams] = useSearchParams();
  /** `?runId=` deep link (from the Start-run drawer): open that run's state on arrival. */
  const deepLinkRunId = searchParams.get('runId');
  const deepLinkHandled = useRef<string | null>(null);
  const [items, setItems] = useState<RunIndexItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [flowFilter, setFlowFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<RunIndexItem | null>(null);
  const [state, setState] = useState<RunStateView | null>(null);
  const [stateError, setStateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listRuns({
        status: status === 'ALL' ? undefined : status,
        flowId: flowFilter || undefined,
        page,
        pageSize: 25,
      });
      setItems(res.items);
      setTotal(res.totalCount);
      setUnavailable(false);
    } catch (e) {
      const err = normalizeApiError(e);
      if (isNotAvailable(err)) {
        setUnavailable(true);
      } else {
        // The failure stays ON the page with a retry — a toast the operator missed is not a state.
        setError(err.message);
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [status, flowFilter, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = useCallback(
    async (r: RunIndexItem) => {
      setSelected(r);
      setState(null);
      setStateError(null);
      try {
        setState(await getRunState(r.runId));
      } catch (e) {
        // The index outlives Temporal retention — the row is still the durable record.
        setStateError(normalizeApiError(e).message);
      }
    },
    [],
  );

  // Deep link: open the named run's drawer once. The index row may lag the runtime by a moment
  // (projection), so the drawer opens on the id alone and reads live state; the row fills in when
  // the index catches up (Refresh).
  useEffect(() => {
    if (!deepLinkRunId || deepLinkHandled.current === deepLinkRunId) return;
    deepLinkHandled.current = deepLinkRunId;
    const row = items.find((r) => r.runId === deepLinkRunId) ?? { runId: deepLinkRunId };
    void open(row);
    const next = new URLSearchParams(searchParams);
    next.delete('runId');
    setSearchParams(next, { replace: true });
  }, [deepLinkRunId, items, open, searchParams, setSearchParams]);

  const columns: ColumnsType<RunIndexItem> = [
    {
      title: 'Run',
      key: 'run',
      render: (_, r) => (
        <Link
          className="ol-mono"
          style={{ fontSize: 11.5 }}
          to={casePath(r.runId, r.entityId)}
          onClick={(e) => e.stopPropagation()}
        >
          {r.runId}
        </Link>
      ),
    },
    {
      title: 'Flow',
      key: 'flow',
      render: (_, r) =>
        r.flowId ? (
          <span>
            {r.flowId}
            {r.flowVersion ? (
              <Text style={{ color: 'var(--ol-text-tertiary)' }}> v{r.flowVersion}</Text>
            ) : null}
          </span>
        ) : (
          <Text style={{ color: 'var(--ol-text-tertiary)' }}>—</Text>
        ),
    },
    {
      title: 'Entity',
      key: 'entity',
      render: (_, r) =>
        r.entityId ? (
          <Link to={`/entities/${encodeURIComponent(r.entityId)}`} className="ol-mono">
            {r.entityId}
          </Link>
        ) : (
          <Text style={{ color: 'var(--ol-text-tertiary)' }}>—</Text>
        ),
    },
    { title: 'Started', key: 'started', render: (_, r) => ts(r.startedAt) },
    { title: 'Completed', key: 'completed', render: (_, r) => ts(r.completedAt) },
    { title: 'Outcome', key: 'outcome', width: 130, render: (_, r) => <OutcomeTag r={r} /> },
  ];

  if (unavailable) {
    return (
      <EmptyState
        icon={<ThunderboltOutlined />}
        title="Flow runs"
        description="The run index is not available in this deployment (flow runtime disabled)."
        bullets={[]}
      />
    );
  }

  return (
    <div className="ol-page">
      <PageHeader
        title="Runs"
        icon={<ThunderboltOutlined />}
        subtitle="Every flow run over this hub — live and historical — from the durable run index. Open a row for the node-by-node state; entity links jump to entity-360."
      />
      <Space style={{ marginBottom: 12 }} wrap>
        <Segmented
          options={['ALL', 'ACTIVE', 'COMPLETED']}
          value={status}
          onChange={(v) => {
            setPage(1);
            setStatus(v as StatusFilter);
          }}
        />
        <Input.Search
          placeholder="Filter by flow id"
          allowClear
          style={{ width: 240 }}
          onSearch={(v) => {
            setPage(1);
            setFlowFilter(v.trim());
          }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>
          Refresh
        </Button>
      </Space>
      <Table<RunIndexItem>
        size="small"
        rowKey={(r) => r.runId}
        columns={columns}
        dataSource={items}
        loading={loading && items.length > 0}
        locale={{
          emptyText: loading ? (
            <TableSkeleton label="Loading runs" />
          ) : error ? (
            <ErrorState what="the run index" detail={error} onRetry={() => void load()} compact />
          ) : (
            <RunsEmptyState filtered={status !== 'ALL' || flowFilter !== ''} />
          ),
        }}
        onRow={(r) => ({ onClick: () => void open(r), style: { cursor: 'pointer' } })}
        pagination={{
          current: page,
          pageSize: 25,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
      />
      <Drawer
        title={
          selected ? (
            <Text className="ol-mono" style={{ fontSize: 12.5 }}>
              {selected.runId}
            </Text>
          ) : (
            ''
          )
        }
        open={selected !== null}
        onClose={() => setSelected(null)}
        width={520}
      >
        {selected && (
          <>
            <Paragraph style={{ marginBottom: 12 }}>
              <OutcomeTag r={selected} />
              {selected.flowId && (
                <Text style={{ marginLeft: 8 }}>
                  {selected.flowId}
                  {selected.flowVersion ? ` v${selected.flowVersion}` : ''}
                </Text>
              )}
            </Paragraph>
            <Paragraph>
              <Link
                to={casePath(selected.runId, selected.entityId)}
                data-testid="open-case"
              >
                Open case
              </Link>
            </Paragraph>
            <Paragraph style={{ fontSize: 12, color: 'var(--ol-text-secondary)' }}>
              Started {ts(selected.startedAt)} · Completed {ts(selected.completedAt)}
              {selected.failure ? ` · failure: ${selected.failure}` : ''}
            </Paragraph>
            {state ? (
              <>
                {state.contentHash && (
                  <Paragraph style={{ fontSize: 11 }} className="ol-mono">
                    artifact {state.contentHash.slice(0, 24)}…
                  </Paragraph>
                )}
                <StateMap label="Node statuses" map={state.nodeStatuses} />
                <StateMap label="Human tasks" map={state.humanTasks} />
              </>
            ) : stateError ? (
              <Paragraph style={{ fontSize: 12, color: 'var(--ol-text-tertiary)' }}>
                Live state unavailable ({stateError}) — the row above is the durable index record.
              </Paragraph>
            ) : (
              <Paragraph style={{ fontSize: 12, color: 'var(--ol-text-tertiary)' }}>
                Loading run state…
              </Paragraph>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}
